import type { ReportGroupBy, StatsReportRow, StatsRow } from '../types';
import { parseFraudScore } from '../../utils/fraudScore';

/** Row from GET /report/?format=json (English keys; RU Excel keys as fallback). */
export interface ReportRow {
  created_day?: string | number | null;
  Дата?: string | number | null;
  messages_count?: number | null;
  'Всего сообщений'?: number | null;
  in_delivery?: number | null;
  'В процессе отправки'?: number | null;
  delivered?: number | null;
  Доставленные?: number | null;
  undelivered?: number | null;
  'Не доставленные'?: number | null;
  expired?: number | null;
  Просроченные?: number | null;
  sell_total?: number | null;
  'Стоимость проданных SMS'?: number | null;
  sell_refund?: number | null;
  'Возмещение КЛИЕНТУ'?: number | null;
  buy_total?: number | null;
  buy_refund?: number | null;
  spent?: number | null;
  profit?: number | null;
  sell_price_per_message?: number | null;
  buy_price_per_message?: number | null;
  fraud_score?: number | null;
  avg_fraud_score?: number | null;
  clicks?: number | null;
  click_rate?: number | null;
  [key: string]: unknown;
}

function num(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function firstNum(row: ReportRow, ...keys: string[]): number {
  for (const k of keys) {
    if (k in row && row[k] != null && row[k] !== '') return num(row[k]);
  }
  return 0;
}

function firstNumOrNull(row: ReportRow, ...keys: string[]): number | null {
  for (const k of keys) {
    if (k in row && row[k] != null && row[k] !== '') {
      const n = typeof row[k] === 'number' ? row[k] : Number(row[k]);
      return typeof n === 'number' && Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function pct(count: number, total: number): number {
  if (!total) return 0;
  return Math.round((count / total) * 1000) / 10;
}

export function formatDateCell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  return String(v);
}

/** Extract row array from API payload; "no data" / empty → []. */
export function normalizeReportPayload(payload: unknown): ReportRow[] {
  if (payload == null) return [];
  if (typeof payload === 'string') {
    const t = payload.trim().toLowerCase();
    if (!t || t === 'no data') return [];
    try {
      return normalizeReportPayload(JSON.parse(payload));
    } catch {
      return [];
    }
  }
  if (Array.isArray(payload)) {
    return payload.filter(
      (row): row is ReportRow =>
        !!row &&
        typeof row === 'object' &&
        !Object.values(row as Record<string, unknown>).some((v) => v === 'Итого:'),
    );
  }
  if (typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    for (const key of ['items', 'rows', 'data', 'results', 'records'] as const) {
      if (Array.isArray(o[key])) return normalizeReportPayload(o[key]);
    }
  }
  return [];
}

/** API `total` object → StatsRow, or null if missing. */
export function extractReportTotal(payload: unknown): StatsRow | null {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const total = (payload as Record<string, unknown>).total;
  if (total == null || typeof total !== 'object' || Array.isArray(total)) return null;
  return mapReportRowToStatsRow(total as ReportRow);
}

export function mapReportRowToStatsRow(row: ReportRow): StatsRow {
  const total = firstNum(row, 'messages_count', 'Всего сообщений');
  const in_delivery = firstNum(row, 'in_delivery', 'В процессе отправки');
  const delivered = firstNum(row, 'delivered', 'Доставленные');
  const undelivered = firstNum(row, 'undelivered', 'Не доставленные');
  const expired = firstNum(row, 'expired', 'Просроченные');
  const sell_total = firstNum(row, 'sell_total', 'Стоимость проданных SMS');
  const sell_refund = firstNum(row, 'sell_refund', 'Возмещение КЛИЕНТУ');
  const buy_total = firstNum(row, 'buy_total');
  const buy_refund = firstNum(row, 'buy_refund');
  const spent =
    row.spent != null
      ? firstNum(row, 'spent')
      : Math.round((buy_total - buy_refund) * 1e5) / 1e5;
  const profit =
    row.profit != null
      ? firstNum(row, 'profit')
      : Math.round((sell_total - sell_refund - spent) * 1e5) / 1e5;
  const dateRaw = row.created_day ?? row['Дата'];
  const fraud_score = parseFraudScore(row.avg_fraud_score ?? row.fraud_score);

  return {
    date: formatDateCell(dateRaw),
    total,
    in_delivery,
    in_delivery_pct: pct(in_delivery, total),
    delivered,
    delivered_pct: pct(delivered, total),
    undelivered,
    undelivered_pct: pct(undelivered, total),
    expired,
    expired_pct: pct(expired, total),
    sms_cost: sell_total,
    refund: sell_refund,
    sell_total,
    sell_refund,
    buy_total,
    buy_refund,
    spent,
    profit,
    sell_price_per_message: firstNumOrNull(row, 'sell_price_per_message'),
    buy_price_per_message: firstNumOrNull(row, 'buy_price_per_message'),
    fraud_score,
    clicks: firstNumOrNull(row, 'clicks'),
    click_rate: firstNumOrNull(row, 'click_rate'),
  };
}

/** Alternate keys the API may use for a group_by dimension. */
const GROUP_FIELD_ALIASES: Record<ReportGroupBy, string[]> = {
  created_day: ['created_day', 'Дата'],
  user_id: ['user_id', 'client_id'],
  manager_id: ['manager_id'],
  partner_id: ['partner_id'],
  carrier_codes: ['carrier_codes'],
  mcc: ['mcc'],
  mnc: ['mnc'],
  aggregator_id: ['aggregator_id'],
  direction_id: ['direction_id'],
  country_iso: ['country_iso'],
  message_type: ['message_type'],
};

export function pickGroupFields(
  row: ReportRow,
  groupBy: ReportGroupBy[],
): Record<string, unknown> {
  const group: Record<string, unknown> = {};
  for (const field of groupBy) {
    const aliases = GROUP_FIELD_ALIASES[field] ?? [field];
    let value: unknown = undefined;
    for (const key of aliases) {
      if (key in row && row[key] != null && row[key] !== '') {
        value = row[key];
        break;
      }
    }
    if (field === 'created_day') {
      group[field] = formatDateCell(value);
    } else {
      group[field] = value ?? '';
    }
  }
  return group;
}

export function mapReportToStatsReportRows(
  payload: unknown,
  groupBy: ReportGroupBy[],
): StatsReportRow[] {
  return normalizeReportPayload(payload).map((row) => {
    const metrics = mapReportRowToStatsRow(row);
    return {
      ...metrics,
      group: pickGroupFields(row, groupBy),
    };
  });
}
